import React, { useEffect, useState, useCallback } from 'react';
import { X, Info, AlertTriangle, CheckCircle2, AlertCircle } from 'lucide-react';
import api from '../lib/api';

const TYPE_STYLES = {
  info: {
    bg: 'bg-blue-50 dark:bg-blue-950/40',
    border: 'border-blue-200 dark:border-blue-800',
    text: 'text-blue-900 dark:text-blue-100',
    icon: Info,
    iconColor: 'text-blue-600 dark:text-blue-400',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-950/40',
    border: 'border-amber-200 dark:border-amber-800',
    text: 'text-amber-900 dark:text-amber-100',
    icon: AlertTriangle,
    iconColor: 'text-amber-600 dark:text-amber-400',
  },
  success: {
    bg: 'bg-emerald-50 dark:bg-emerald-950/40',
    border: 'border-emerald-200 dark:border-emerald-800',
    text: 'text-emerald-900 dark:text-emerald-100',
    icon: CheckCircle2,
    iconColor: 'text-emerald-600 dark:text-emerald-400',
  },
  error: {
    bg: 'bg-rose-50 dark:bg-rose-950/40',
    border: 'border-rose-200 dark:border-rose-800',
    text: 'text-rose-900 dark:text-rose-100',
    icon: AlertCircle,
    iconColor: 'text-rose-600 dark:text-rose-400',
  },
};

const DISMISSED_KEY = 'helios_dismissed_banners';

const getDismissed = () => {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]');
  } catch {
    return [];
  }
};

const setDismissed = (ids) => {
  localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids));
};

export const BannerBar = ({ endpoint = '/banners' }) => {
  const [banners, setBanners] = useState([]);
  const [dismissed, setDismissedState] = useState(getDismissed());

  const fetchBanners = useCallback(async () => {
    try {
      const res = await api.get(endpoint);
      setBanners(res.data || []);
    } catch {
      setBanners([]);
    }
  }, [endpoint]);

  useEffect(() => {
    fetchBanners();
    const iv = setInterval(fetchBanners, 60000);
    return () => clearInterval(iv);
  }, [fetchBanners]);

  const handleDismiss = (id) => {
    const next = [...new Set([...dismissed, id])];
    setDismissed(next);
    setDismissedState(next);
  };

  const visible = banners.filter((b) => !dismissed.includes(b.id));
  if (visible.length === 0) return null;

  return (
    <div className="flex flex-col" data-testid="banner-bar">
      {visible.map((b) => {
        const style = TYPE_STYLES[b.type] || TYPE_STYLES.info;
        const Icon = style.icon;
        return (
          <div
            key={b.id}
            className={`${style.bg} ${style.border} ${style.text} border-b px-4 py-2.5 flex items-start gap-3`}
            data-testid={`banner-${b.id}`}
          >
            <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${style.iconColor}`} />
            <div className="flex-1 min-w-0 text-sm">
              {b.title && <span className="font-semibold mr-2">{b.title}</span>}
              <span>{b.message}</span>
            </div>
            {b.dismissible && (
              <button
                type="button"
                onClick={() => handleDismiss(b.id)}
                className="flex-shrink-0 p-1 rounded hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                aria-label="Dismiss banner"
                data-testid={`banner-dismiss-${b.id}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};
